import { loadPermissionById } from "@/redux/rtk/features/auth/authSlice";
import { addUser } from "@/redux/rtk/features/hrm/user/userSlice";
import { getSetting } from "@/redux/rtk/features/setting/settingSlice";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoginTable from "../Card/LoginTable";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [defaultValue, setDefaultValue] = useState("");
  const [imageError, setImageError] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, loading: settingLoading } =
    useSelector((state) => state?.setting) || {};

  const setValue = (data) => {
    if (data && data.length > 0) {
      const selectedUser = data[0];
      setUsername(selectedUser.username || "");
      setPassword(selectedUser.password || "");
      setErrors({});
      setDefaultValue(data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!username) newErrors.username = "Please input your username!";
    if (!password) newErrors.password = "Please input password!";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const values = { username, password };
      const resp = await dispatch(addUser(values));
      if (resp.payload.message === "success") {
        dispatch(getSetting());
        dispatch(loadPermissionById(resp.payload?.data?.roleId));
      } else {
        setLoading(false);
        setErrors({ general: "Login failed. Please try again." });
      }
    } catch {
      setLoading(false);
      setErrors({ general: "An error occurred. Please try again." });
    }
  };

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  useEffect(() => {
    if (isLogged) navigate("/admin");
  }, [isLogged, navigate]);

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setUsername(defaultValue[0].username || "");
      setPassword(defaultValue[0].password || "");
    }
  }, [defaultValue]);

  return (
    <div className="relative h-screen overflow-hidden bg-[#f4f7fb]">
      {/* Background */}
      <div className="pointer-events-none absolute inset-y-0 left-[-16%] w-[52%] -skew-x-[28deg] origin-left bg-gradient-to-br from-[#15B983] via-[#15B983] to-[#15B983]" />

      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 left-6 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-4rem] left-[12%] w-[28rem] h-[28rem] bg-white/10 rounded-full blur-3xl" />

      {/* Grid layout */}
      <div className="relative flex h-screen items-center justify-center">        {/* LEFT PANEL */}

        {/* RIGHT PANEL */}
        <div className="w-full max-w-md flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl scale-[0.96] sm:scale-100">
            {/* Logo */}
            {/* Logo */}
            <div className="mb-6">
              <div className="w-[160px] h-[60px] mx-auto flex items-center justify-center mb-3">
                <img
                  alt="logo"
                  src="/images/HRMSmart-Logo.png"
                  style={{ width: "160px", height: "60px", objectFit: "contain" }}
                />
              </div>
            </div>

            {/* Welcome */}
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Welcome Back</h1>
              <p className="text-gray-600 text-sm">
                Enter your credentials to access your account
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.general && (
                <div className="p-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg">
                  {errors.general}
                </div>
              )}

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserOutlined className="text-gray-400 text-sm" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        username: "",
                        general: "",
                      }));
                    }}
                    placeholder="Enter your username"
                    className={`w-full pl-8 pr-3 py-2 border ${errors.username ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:ring-2 focus:ring-[#15B983] focus:border-transparent outline-none text-sm`}
                  />
                </div>
                {errors.username && (
                  <p className="mt-1 text-xs text-red-600">{errors.username}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockOutlined className="text-gray-400 text-sm" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        password: "",
                        general: "",
                      }));
                    }}
                    placeholder="Enter your password"
                    className={`w-full pl-8 pr-9 py-2 border ${errors.password ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:ring-2 focus:ring-[#15B983] focus:border-transparent outline-none text-sm`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeInvisibleOutlined className="text-gray-400 text-sm hover:text-gray-600" />
                    ) : (
                      <EyeOutlined className="text-gray-400 text-sm hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full text-white py-2.5 rounded-lg font-semibold bg-[#15B983] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-lg"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            {/* Demo Quick Access (optional) */}
            {import.meta.env.VITE_LOGIN_TABLE === "true" && (
              <div className="mt-6">
                <hr className="border-t border-gray-100" />
                <div className="mt-2 text-center text-gray-600 text-xs font-semibold mb-2">
                  Demo Quick Access
                </div>
                <LoginTable setDefaultValue={setValue} />
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
